import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'
import { useOrganization } from '@/contexts/OrganizationContext'
import { decodeRelayId } from '@/utils/id'

/**
 * Provider profile data from the new backend.
 * Based on ProfileProviderType in accounts/schema.py
 */
export interface OrganizationProviderRow {
  id: string
  // User-derived fields
  fullName?: string | null
  name?: string | null // Alias for backwards compatibility
  providerName?: string | null
  phoneNumber?: string | null
  email?: string | null
  isVerified?: boolean | null
  
  // Provider type
  providerType?: string | null
  providerTypeDisplay?: string | null
  
  // Status fields
  status?: string | null
  statusDisplay?: string | null
  
  // Availability
  isAvailable?: boolean | null
  availabilityStatus?: string | null
  availabilityStatusDisplay?: string | null
  
  // Ratings
  rating?: number | null
  averageRating?: number | null
  totalReviews?: number | null
  ratingDisplay?: string | null
  
  // Organization/Entity info
  organizationName?: string | null
  companyName?: string | null
  
  // License info
  licenseExpiryDate?: string | null
  isLicenseExpired?: boolean | null
  daysUntilLicenseExpiry?: number | null
  
  // Background check
  backgroundCheckStatus?: string | null
  backgroundCheckStatusDisplay?: string | null
  isBackgroundCheckValid?: boolean | null
  
  // Service statistics
  totalAssignedServices?: number | null
  activeServicesCount?: number | null
  completedServicesCount?: number | null
  pendingAcceptanceCount?: number | null

  // Services this provider is authorized to offer (ProfileProvider.capable_services).
  // Dispatch gates on this set fail-closed.
  capableServices?: Array<{ id: string; name: string; label?: string | null }> | null
  
  // Timestamps
  createdAt?: string | null
  updatedAt?: string | null
}

/**
 * GraphQL query for fetching organization providers.
 * Matches the ProfileProviderType fields in the new backend's accounts/schema.py
 */
const ORGANIZATION_PROVIDERS_QUERY = `
  query OrganizationProviders($organizationId: ID!) {
    organizationProviders(organizationId: $organizationId) {
      id
      fullName
      providerName
      phoneNumber
      email
      isVerified
      
      providerType
      providerTypeDisplay
      
      status
      statusDisplay
      
      isAvailable
      availabilityStatus
      availabilityStatusDisplay
      
      rating
      averageRating
      totalReviews
      ratingDisplay
      
      organizationName
      companyName
      
      licenseExpiryDate
      isLicenseExpired
      daysUntilLicenseExpiry
      
      backgroundCheckStatus
      backgroundCheckStatusDisplay
      isBackgroundCheckValid
      
      totalAssignedServices
      activeServicesCount
      completedServicesCount
      pendingAcceptanceCount

      capableServices {
        id
        name
        label
      }

      createdAt
      updatedAt
    }
  }
`

interface OrganizationProvidersResponse {
  organizationProviders: OrganizationProviderRow[] | null
}

/**
 * Hook to fetch providers for the current organization.
 * Uses GraphQL to query the new backend's ProfileProvider model.
 * 
 * Backend endpoint: POST /graphql/
 * Query: organizationProviders(organizationId: ID!)
 * 
 * The backend filters providers by:
 * - entity__company_id (new structure)
 * - company_id (legacy fallback)
 */
export function useOrganizationProviders() {
  const { currentOrganization } = useOrganization()

  const query = useQuery({
    queryKey: ['organizationProviders', currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization?.id) return []
      // Backend resolver expects UUID; OrganizationContext may store Relay global ID.
      const organizationId = decodeRelayId(currentOrganization.id) ?? currentOrganization.id
      const response = await graphqlQuery<OrganizationProvidersResponse>(ORGANIZATION_PROVIDERS_QUERY, {
        organizationId,
      })
      
      // Transform data if needed and add backwards compatibility
      const providers = response.data?.organizationProviders ?? []
      return providers.map(p => ({
        ...p,
        // Ensure name field is populated for backwards compatibility
        name: p.name || p.fullName || p.providerName,
        // Normalize rating
        rating: p.rating ?? p.averageRating ?? null,
      }))
    },
    enabled: !!currentOrganization?.id,
    staleTime: 1000 * 60, // 1 minute
  })

  return {
    providers: query.data || [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

